/* The quiz funnel's rules (sections/st-funnel.liquid): answers in, setups out. Pure, no DOM, so the
   same file runs in the page (window.stFunnelRules) and in a Node sandbox in scripts/gates/funnel-rules.mjs,
   which fails CI when a case in scripts/gates/funnel-cases.json stops giving its expected setup.

   The catalogue is read from the store (snippets/st-funnel-data.liquid): each product has a platform
   (its platform collection) and variants whose "Bundle" option starts with the pack size; its format
   comes from a word in its handle (cfg.words, the section's "Formats by handle"). Nothing here names
   a product or a price.

   Shop route: the tap spots give the formats, the size band gives each format's pack. A format a
   platform does not come in is swapped along cfg.fallback, with a note. Three setups:
     starter      the first format
     recommended  starter plus the next format (a chosen one, else the next complement), plus a
                  stand for the second platform ticked
     complete     recommended plus one more format, plus a stand for every other platform ticked
   Volume route: each format for each platform at the chosen pack (100 or 500), bought outright
   when the design is standard and the pack exists; everything else is a quote. */
(function (root, factory) {
  root.stFunnelRules = factory();
})(globalThis, function () {
  // The pack option is "Bundle", and the last option when its name is translated ("Paquete" on /es).
  const bundleAt = (product) => {
    const i = product.opts.indexOf('Bundle');
    return i >= 0 ? i : product.opts.length - 1;
  };
  const packOf = (product, variant) => {
    const n = parseInt(variant.o[bundleAt(product)], 10);
    return Number.isFinite(n) ? n : 1;
  };

  // A product's format is the first "word: format" line whose word is in its handle (handles are not
  // translated; product types are). A product no line matches is left out.
  const formatOf = (handle, words) => {
    for (const line of String(words || '').split(/\r?\n/)) {
      const [word, format] = line.split(':').map((x) => (x || '').trim().toLowerCase());
      if (word && format && handle.includes(word)) return format;
    }
    return '';
  };
  const prepare = (cat, cfg) => {
    if (cat.prepared) return cat;
    cat.products = cat.products.map((p) => ({ ...p, format: p.format || formatOf(p.h, cfg.words) })).filter((p) => p.format);
    cat.prepared = true;
    return cat;
  };

  const find = (cat, format, platform) => cat.products.find((p) => p.format === format && p.platform === platform);

  // A format the platform lacks becomes the first fallback it has; the note says so.
  const resolve = (cat, cfg, format, platform) => {
    if (find(cat, format, platform)) return { format };
    for (const alt of cfg.fallback[format] || []) {
      if (find(cat, alt, platform)) return { format: alt, note: { from: format, to: alt, platform } };
    }
    return null;
  };

  // The packs a product offers in its default look (the first available variant's other options).
  const packsOf = (product) => {
    const first = product.v.find((v) => v.a) || product.v[0];
    if (!first) return [];
    const bi = bundleAt(product);
    const same = (v) => v.o.every((o, i) => i === bi || o === first.o[i]);
    return product.v
      .filter((v) => v.a && same(v))
      .map((v) => ({ id: v.id, n: packOf(product, v), price: v.p }))
      .sort((a, b) => a.n - b.n);
  };

  // Exact pack, else the smallest larger one, else the largest smaller one.
  const pick = (packs, n) =>
    packs.find((p) => p.n === n) || packs.find((p) => p.n > n) || packs.slice().reverse().find((p) => p.n < n) || null;

  const item = (cat, format, platform, n) => {
    const product = find(cat, format, platform);
    if (!product) return null;
    const packs = packsOf(product);
    const chosen = pick(packs, n);
    if (!chosen) return null;
    const out = { format, platform, h: product.h, title: product.title, img: product.img, id: chosen.id, n: chosen.n, price: chosen.price, packs };
    const metal = format === 'card' && find(cat, 'metal', platform);
    if (metal) {
      const mp = packsOf(metal);
      const m = mp.find((p) => p.n === chosen.n);
      if (m) out.upgrade = { format: 'metal', platform, h: metal.h, title: metal.title, img: metal.img, id: m.id, n: m.n, price: m.price, packs: mp };
    }
    return out;
  };

  const total = (items) => items.reduce((sum, i) => sum + i.price, 0);

  function shop(answers, cat, cfg) {
    const notes = [];
    const platforms = answers.platforms.filter((p) => cat.products.some((x) => x.platform === p));
    const primary = platforms[0];
    const second = platforms[1];
    const band = Math.max(0, Math.min(3, answers.band | 0));
    const qty = (format, chosen) =>
      chosen ? cfg.packs[format][band] : format === 'stand' || format === 'plaque' ? 1 : cfg.packs[format][band];

    const formats = [];
    const add = (format, chosen) => {
      const r = resolve(cat, cfg, format, primary);
      if (!r) return;
      if (r.note && chosen) notes.push(r.note);
      if (!formats.some((f) => f.format === r.format)) formats.push({ format: r.format, chosen });
    };
    answers.formats.forEach((f) => add(f, true));
    if (!formats.length) add('card', true);
    const chosenCount = formats.length;
    cfg.complements.forEach((f) => add(f, false));

    const build = (list) => list.map((f) => item(cat, f.format, primary, qty(f.format, f.chosen))).filter(Boolean);
    // Every platform the shopper ticked gets at least a stand: the second in Recommended, every other
    // one in Complete (Matt, 2026-10-11: a setup "built from your answers" kept dropping a platform).
    const standFor = (platform) => {
      const r = resolve(cat, cfg, 'stand', platform);
      return r && item(cat, r.format, platform, 1);
    };
    const extras = platforms.slice(1).map(standFor).filter(Boolean);
    const starter = build(formats.slice(0, 1));
    const recommended = build(formats.slice(0, 2)).concat(extras.slice(0, 1));
    const complete = build(formats.slice(0, 3)).concat(extras);
    if (answers.formats.includes('stand') && band === 3) notes.push({ more: 'stand' });

    const setups = [
      { tier: 'starter', items: starter, pct: 0 },
      { tier: 'recommended', items: recommended, pct: cfg.disc.recommended || 0 },
      { tier: 'complete', items: complete, pct: cfg.disc.complete || 0 },
    ].map((s) => ({ ...s, total: total(s.items) }));
    return { route: 'shop', primary, second, chosen: chosenCount, notes, setups };
  }

  function volume(answers, cat, cfg) {
    const notes = [];
    const items = [];
    const missing = [];
    const n = answers.units;
    const platforms = answers.platforms.filter((p) => cat.products.some((x) => x.platform === p));
    const formats = answers.formats.length ? answers.formats : ['card'];
    for (const platform of platforms) {
      for (const f of formats) {
        const r = resolve(cat, cfg, f, platform);
        if (!r) continue;
        if (r.note) notes.push(r.note);
        const product = find(cat, r.format, platform);
        const exact = typeof n === 'number' && packsOf(product).find((p) => p.n === n);
        if (exact && !items.some((i) => i.id === exact.id)) {
          items.push({ format: r.format, platform, h: product.h, title: product.title, img: product.img, id: exact.id, n: exact.n, price: exact.price, packs: packsOf(product) });
        } else if (!exact) missing.push({ format: r.format, platform });
      }
    }
    const reasons = [];
    if (answers.design === 'logo') reasons.push('logo');
    if (typeof n !== 'number') reasons.push('more');
    if (missing.length && typeof n === 'number') reasons.push('pack');
    const buy = answers.design !== 'logo' && typeof n === 'number' && items.length > 0;
    return { route: 'volume', notes, items: buy ? items : [], total: buy ? total(items) : 0, missing, reasons, quote: reasons.length > 0 || !buy };
  }

  // The line properties for adding variant id: the Preorder property when the catalogue marks it a
  // preorder (snippets/st-preorder.liquid), so the funnel and the quote quiz tag it as every other
  // add to cart does; else none.
  const propertiesFor = (cat, id) => {
    const pre = cat.preorder?.prop && cat.products.some((p) => p.v.some((v) => v.id === id && v.pre));
    return pre ? { [cat.preorder.prop]: cat.preorder.value || '' } : {};
  };

  return {
    packOf,
    packsOf,
    formatOf,
    propertiesFor,
    build(answers, cat, cfg) {
      prepare(cat, cfg);
      return answers.route === 'volume' ? volume(answers, cat, cfg) : shop(answers, cat, cfg);
    },
  };
});
